import React, { useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Menu as MenuIcon, ChevronLeft } from "@material-ui/icons";
import { nodeCategories, MIME_NODO_PALETA } from "../FlowBuilder/nodeCategoriesConfig";
import { useStyles } from "../FlowBuilder/flowEditorStyles";

/** Preferencia del usuario: panel plegado o no. Solo comodidad: si el navegador no deja guardar, no pasa nada. */
const CLAVE_PLEGADO = "flowbuilder.paleta.plegada";
const leerPlegado = () => {
  try {
    return window.localStorage.getItem(CLAVE_PLEGADO) === "1";
  } catch {
    return false;
  }
};
const guardarPlegado = (valor) => {
  try {
    window.localStorage.setItem(CLAVE_PLEGADO, valor ? "1" : "0");
  } catch {
    /* sin almacenamiento: la preferencia dura lo que la sesión */
  }
};

/**
 * Paleta de nodos (0422 QA-UX).
 *  - Cada tarjeta se ARRASTRA al lienzo y el nodo cae donde se suelta; el clic sigue sirviendo y
 *    lo coloca en el centro de lo que se está viendo.
 *  - El botón de hamburguesa pliega el panel a una columna de iconos (el nombre sale al pasar el
 *    ratón) para dejar más sitio al lienzo en flujos grandes.
 */
const NodePalette = ({ onAddNode, mobileOpen, onDrawerClose, provider }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [plegado, setPlegado] = useState(leerPlegado);

  const alternarPlegado = () => {
    setPlegado((p) => {
      guardarPlegado(!p);
      return !p;
    });
  };

  // Nodos marcados wabaOnly solo se muestran en flujos WABA (0371 WF5).
  const isWaba = String(provider || "").toLowerCase() === "waba";
  const isVisible = (node) => !node.wabaOnly || isWaba;

  const colorDe = (categoryKey) =>
    categoryKey === "bubbles"
      ? theme.palette.type === "dark"
        ? "white"
        : theme.palette.primary.main
      : categoryKey === "inputs"
      ? "#ff6b35"
      : categoryKey === "contenedor"
      ? "#5c35d4"
      : "#9c88ff";

  const alEmpezarArrastre = (e, tipo) => {
    e.dataTransfer.setData(MIME_NODO_PALETA, tipo);
    e.dataTransfer.effectAllowed = "move";
  };

  const renderNodes = (compacto, arrastrable) => (
    <>
      {Object.entries(nodeCategories).map(([categoryKey, category]) => {
        const nodes = category.nodes.filter(isVisible);
        if (nodes.length === 0) return null;
        return (
          <Box key={categoryKey}>
            {compacto ? (
              <Divider className={classes.paletteDivider} />
            ) : (
              <Typography className={classes.categoryTitle}>{category.title}</Typography>
            )}
            {nodes.map((node) => {
              const tarjeta = (
                <Card
                  key={node.type}
                  className={`${classes.nodeCard}${compacto ? ` ${classes.nodeCardCompacta}` : ""}`}
                  onClick={() => onAddNode(node.type)}
                  draggable={arrastrable}
                  onDragStart={arrastrable ? (e) => alEmpezarArrastre(e, node.type) : undefined}
                  elevation={0}
                >
                  <CardContent
                    className={compacto ? classes.nodeCardContentCompacta : classes.nodeCardContent}
                  >
                    <Box style={{ color: colorDe(categoryKey), display: "flex", alignItems: "center" }}>
                      {node.icon}
                    </Box>
                    {!compacto && (
                      <Typography className={classes.nodeLabel}>{node.label}</Typography>
                    )}
                  </CardContent>
                </Card>
              );
              return compacto ? (
                <Tooltip key={node.type} title={node.label} placement="right" arrow>
                  {tarjeta}
                </Tooltip>
              ) : (
                tarjeta
              );
            })}
          </Box>
        );
      })}
    </>
  );

  return (
    <>
      {/* Sidebar desktop/tablet — hidden on xs via CSS */}
      <Paper
        className={`${classes.sidebar}${plegado ? ` ${classes.sidebarPlegado}` : ""}`}
        elevation={0}
      >
        <Box className={plegado ? classes.paletteHeaderCompacta : classes.paletteHeader}>
          {!plegado && (
            <Typography className={classes.paletteHint}>
              Arrastra al lienzo o haz clic
            </Typography>
          )}
          <Tooltip title={plegado ? "Mostrar nombres" : "Solo iconos"} placement="right">
            <IconButton size="small" onClick={alternarPlegado}>
              {plegado ? <MenuIcon fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        {renderNodes(plegado, true)}
      </Paper>

      {/* Drawer mobile — xs only: en táctil no hay arrastre HTML5, se añade con un toque */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onDrawerClose}
        ModalProps={{ keepMounted: true }}
      >
        <Box className={classes.sidebarContent} onClick={onDrawerClose}>
          {renderNodes(false, false)}
        </Box>
      </Drawer>
    </>
  );
};

export default NodePalette;
